import { useMutation } from '@apollo/client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { Icon, IconClose } from '@flixbus/honeycomb-icons-react';
import {
	Input,
	Heading,
	Button,
	Select,
	SelectOption,
	Panel,
	PanelHeader,
	PanelContent,
	Popup,
	PopupSection,
	Text,
	PanelFooter,
	PanelFooterColumn,
} from '@flixbus/honeycomb-react';

import { UPDATE_CREW_MEMBER } from 'pages/CrewMemberDetails/gql/updateCrewMemberInfo';
import { constructEditDriverData } from 'utils/constructEditDriverInfo';

import { buildUpdateCrewMemberInput } from './buildUpdateCrewMemberInput';
import { formatEffectiveDate, homeLocationName } from './formatEffectiveDate';
import { splitFullName } from './splitFullName';
import { EDIT_INFO_TEXT } from './text';

import styles from './styles.module.css';

const EditInfo = ({ isShown, onClose, driverInfo, id, onSuccess, onError }) => {
	const targetRef = useRef(null);
	const [updateDriver, { loading }] = useMutation(UPDATE_CREW_MEMBER, {
		refetchQueries: ['GetCrewMember'],
		onCompleted: () => {
			onSuccess();
		},
		onError: () => {
			onError();
			reset({ ...constructEditDriverData(driverInfo) });
		},
	});

	const [isPopupShown, setIsPopupShown] = useState(false);

	const {
		control,
		handleSubmit,
		resetField,
		reset,
		getValues,
		formState: { dirtyFields, isValid },
	} = useForm({
		mode: 'onChange',
		defaultValues: useMemo(() => {
			return { ...constructEditDriverData(driverInfo) };
		}, [driverInfo]),
	});

	const closePanel = () => {
		const isDirty = !!Object.keys(dirtyFields).length || !isValid;
		if (isDirty) {
			setIsPopupShown(true);
			return;
		}
		onClose();
	};

	const declineAndClose = () => {
		reset({ ...constructEditDriverData(driverInfo) });
		setIsPopupShown(false);
		onClose();
	};

	useEffect(() => {
		reset({ ...constructEditDriverData(driverInfo) });
	}, [driverInfo, reset]);

	const onSubmit = () => {
		const formValues = getValues();
		updateDriver({
			variables: {
				id,
				input: buildUpdateCrewMemberInput({ formValues, driverInfo }),
			},
		});
	};

	const resetIcon = (fieldName) => (
		<Button onClick={() => resetField(fieldName)}>
			<Icon InlineIcon={IconClose} />
		</Button>
	);

	return (
		<>
			<Panel
				id="edit-panel"
				active={isShown}
				hasOverlay
				onOverlayClick={closePanel}
				targetRef={targetRef}
			>
				<PanelHeader
					closeButtonProps={{ label: EDIT_INFO_TEXT.closeButtonLabel, onClick: closePanel }}
				>
					{EDIT_INFO_TEXT.panelHeader}
				</PanelHeader>
				<PanelContent>
					<form className={styles.vertical} onSubmit={handleSubmit(onSubmit)}>
						<Heading size={4}>{EDIT_INFO_TEXT.generalDataHeading}</Heading>

						<Input
							disabled
							id="firstName"
							label={EDIT_INFO_TEXT.firstNameLabel}
							value={splitFullName(driverInfo.fullName).first}
							onChange={() => {}}
						/>
						<Input
							disabled
							id="lastName"
							label={EDIT_INFO_TEXT.lastNameLabel}
							value={splitFullName(driverInfo.fullName).last}
							onChange={() => {}}
						/>

						<div className={styles.horizontal}>
							<Input
								disabled
								id="homeLocation"
								label={EDIT_INFO_TEXT.homeLocationLabel}
								placeholder={EDIT_INFO_TEXT.homeLocationPlaceholder}
								value={homeLocationName(driverInfo.permanentHomeBase)}
								onChange={() => {}}
							/>
							<Input
								disabled
								id="effectiveDate"
								label={EDIT_INFO_TEXT.effectiveDateLabel}
								placeholder={EDIT_INFO_TEXT.dateShortPlaceholder}
								value={formatEffectiveDate(driverInfo.permanentHomeBase)}
								onChange={() => {}}
							/>
						</div>

						<Controller
							disabled={loading}
							name="phone"
							control={control}
							rules={{
								maxLength: 13,
							}}
							render={({ field, fieldState }) => {
								const { isDirty, invalid } = fieldState;
								const inputProps = {
									ref: null,
									id: field.name,
									label: EDIT_INFO_TEXT.phoneLabel,
									placeholder: '',
									infoError: EDIT_INFO_TEXT.phoneTooLongError,
								};
								if (isDirty) {
									inputProps.valid = !invalid;
									inputProps.iconRight = resetIcon(field.name);
								}
								return <Input {...field} {...inputProps} />;
							}}
						/>

						<Heading size={4}>{EDIT_INFO_TEXT.employmentDataHeading}</Heading>

						<Select
							disabled
							id="employmentType"
							label={EDIT_INFO_TEXT.employmentTypeLabel}
							placeholder={EDIT_INFO_TEXT.employmentTypePlaceholder}
							value={'fullTime'}
						>
							<SelectOption value={'fullTime'}>{EDIT_INFO_TEXT.employmentTypeMocked}</SelectOption>
						</Select>

						<Controller
							disabled={loading}
							name="startTime"
							control={control}
							render={({ field, fieldState }) => {
								const { isDirty, invalid } = fieldState;
								const inputProps = {
									ref: null,
									id: field.name,
									label: EDIT_INFO_TEXT.startTimeLabel,
									placeholder: EDIT_INFO_TEXT.startTimePlaceholder,
									infoError: EDIT_INFO_TEXT.fieldError,
									type: 'date',
								};
								if (isDirty) {
									inputProps.valid = !invalid;
									inputProps.iconRight = resetIcon(field.name);
								}
								return <Input {...field} {...inputProps} />;
							}}
						/>
						<Controller
							disabled={loading}
							name="driverLicenseExpiresAt"
							control={control}
							render={({ field, fieldState }) => {
								const { isDirty, invalid } = fieldState;
								const inputProps = {
									ref: null,
									id: field.name,
									label: EDIT_INFO_TEXT.driverLicenseLabel,
									placeholder: EDIT_INFO_TEXT.dateShortPlaceholder,
									infoError: EDIT_INFO_TEXT.fieldError,
									type: 'date',
								};
								if (isDirty) {
									inputProps.valid = !invalid;
									inputProps.iconRight = resetIcon(field.name);
								}
								return <Input {...field} {...inputProps} />;
							}}
						/>
						<Controller
							disabled={loading}
							name="medicalCheckExpiresAt"
							control={control}
							render={({ field, fieldState }) => {
								const { isDirty, invalid } = fieldState;
								const inputProps = {
									ref: null,
									id: field.name,
									label: EDIT_INFO_TEXT.medicalCheckLabel,
									placeholder: EDIT_INFO_TEXT.dateShortPlaceholder,
									infoError: EDIT_INFO_TEXT.fieldError,
									type: 'date',
								};
								if (isDirty) {
									inputProps.valid = !invalid;
									inputProps.iconRight = resetIcon(field.name);
								}
								return <Input {...field} {...inputProps} />;
							}}
						/>
					</form>
				</PanelContent>
				<PanelFooter>
					<PanelFooterColumn>
						<Button
							extraClasses={styles.buttonBottom}
							loading={loading}
							display="block"
							appearance="primary"
							id="saveDetails"
							type="submit"
							disabled={!Object.keys(dirtyFields).length || !isValid}
							onClick={() => onSubmit()}
						>
							{EDIT_INFO_TEXT.saveButton}
						</Button>
					</PanelFooterColumn>
				</PanelFooter>
			</Panel>
			<Popup id="unsaved-popup" style={{ zIndex: '10000' }} active={isPopupShown}>
				<PopupSection>
					<h1>{EDIT_INFO_TEXT.unsavedPopupTitle}</h1>
				</PopupSection>
				<PopupSection>
					<Text>{EDIT_INFO_TEXT.unsavedPopupBody}</Text>
				</PopupSection>
				<PopupSection>
					<Button onClick={() => setIsPopupShown(false)}>
						{EDIT_INFO_TEXT.unsavedGoBackButton}
					</Button>
					<Button onClick={declineAndClose} appearance="danger">
						{EDIT_INFO_TEXT.unsavedCloseButton}
					</Button>
				</PopupSection>
			</Popup>
		</>
	);
};

export default EditInfo;
